import { useQuery } from "@apollo/client/react";
import { useEffect, useState } from "react";
import {
  BellIcon,
  CircleCheckIcon,
  MailIcon,
  MailOpenIcon,
} from "lucide-react";

import {
  BulkOperationEnum,
  ExecutionStatusEnum,
  InboxQuery,
  NotificationFilter,
} from "@/__generated__/graphql";
import { Icon } from "@/components/atoms/Icon";
import { Popper } from "@/components/atoms/Popper";
import {
  SelectableCardList,
  SelectableCardListSearchField,
} from "@/components/molecules/SelectableCardList";
import {
  SelectableListItemRow,
  SelectableListItemRowOverline,
  SelectableListItemRowTitleEnd,
} from "@/components/molecules/SelectableListItemRow";
import { Mutations, Queries } from "@/constants";
import { useBackendMutation } from "@/hooks/useBackendMutation";
import { useScope } from "@/hooks/useScope";
import { State } from "@/state";
import { Utils } from "@/utils";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { cn } from "@/components/ui/utils";

type InboxItem = NonNullable<
  InboxQuery["notificationsConnection"]["data"]
>["edges"][number]["node"];

/** What the list's rows are while it loads: a typical notification. */
const NOTIFICATION_PLACEHOLDER = {
  id: "placeholder",
  identifier: "placeholder",
  createdAt: "2026-01-01T00:00:00Z",
  message: "Transactions is ready: 1,204 rows.",
  isRead: true,
  bulkOperation: {
    id: "placeholder",
    identifier: "placeholder",
    operation: BulkOperationEnum.Export,
    name: "Transactions",
    status: ExecutionStatusEnum.Succeeded,
    rowCount: 1204,
    entities: [
      {
        id: "placeholder",
        identifier: "placeholder",
        name: "Elm Street Coffee",
      },
    ],
    file: { name: "transactions.csv", url: "#" },
    spreadsheetUrl: null,
  },
} as InboxItem;

/** Notifications asked for at a time; more come as the list is scrolled. */
const PAGE_SIZE = 20;

/** How often they are asked about again while a bulk operation is under way. */
const WRITING_POLL_MS = 3000;

/** How long typing settles before the list is searched. */
const SEARCH_DEBOUNCE_MS = 300;

/**
 * The asker's notifications: what has come of what they asked for - a list
 * exported, with its file to download, or deleted, or neither - and whose rows
 * it reached; and an email forwarded to a business they are in, whose chat it
 * opens, where what was filed from it waits to be confirmed. Kept
 * in the asker's own entity, so the bell reads that one, whatever the app is
 * scoped to. Each is acknowledged, or set back to unread, on its own, and
 * the list shows all of them, or only the unread or the read.
 */
export function AppTopbarNotificationsPopper() {
  const { userEntity } = useScope();
  const entityIdentifier = userEntity?.identifier;
  const entityIdentifiers = entityIdentifier ? [entityIdentifier] : [];
  const [isOpen, setIsOpen] = useState(false);
  const [term, setTerm] = useState("");
  const [search, setSearch] = useState("");
  const [shown, setShown] = useState<"all" | "unread" | "read">("all");
  useEffect(() => {
    const timeout = setTimeout(
      () => setSearch(term.trim()),
      SEARCH_DEBOUNCE_MS,
    );
    return () => clearTimeout(timeout);
  }, [term]);

  // How many are unread, however many there are - not the loaded page's.
  const unreadQuery = useQuery(Queries.INBOX_CONNECTION_QUERY, {
    variables: {
      entityIdentifiers,
      filter: { field: { isRead: { exact: false } } },
      first: 1,
    },
    skip: !entityIdentifier,
    fetchPolicy: "cache-and-network",
  });
  const unread =
    unreadQuery.data?.notificationsConnection?.data?.totalCount ?? 0;

  // One lookup a condition: what it says, and whether it has been read.
  const conditions: NotificationFilter[] = [
    ...(search ? [{ field: { message: { iContains: search } } }] : []),
    ...(shown === "all"
      ? []
      : [{ field: { isRead: { exact: shown === "read" } } }]),
  ];
  const { data, loading, fetchMore, startPolling, stopPolling } = useQuery(
    Queries.INBOX_CONNECTION_QUERY,
    {
      variables: {
        entityIdentifiers,
        filter:
          conditions.length === 0
            ? null
            : conditions.length === 1
              ? conditions[0]
              : { all: conditions },
        first: PAGE_SIZE,
      },
      skip: !entityIdentifier,
      fetchPolicy: "cache-and-network",
    },
  );
  const page = data?.notificationsConnection?.data;
  const notifications = page?.edges.map((edge) => edge.node) ?? [];

  // Asked again while a bulk operation is under way, so what came of it -
  // an export's file - shows up here the moment it has - and not at all otherwise.
  const isWriting = notifications.some(
    (notification) =>
      notification.bulkOperation?.status === ExecutionStatusEnum.Queued ||
      notification.bulkOperation?.status === ExecutionStatusEnum.Running,
  );
  const { startPolling: startPollingUnread, stopPolling: stopPollingUnread } =
    unreadQuery;
  useEffect(() => {
    if (!isWriting) {
      return;
    }
    startPolling(WRITING_POLL_MS);
    startPollingUnread(WRITING_POLL_MS);
    return () => {
      stopPolling();
      stopPollingUnread();
    };
  }, [
    isWriting,
    startPolling,
    stopPolling,
    startPollingUnread,
    stopPollingUnread,
  ]);

  const { mutate: markRead } = useBackendMutation({
    mutation: Mutations.MARK_NOTIFICATION_READ,
  });
  const { mutate: markAllRead } = useBackendMutation({
    mutation: Mutations.MARK_ALL_NOTIFICATIONS_AS_READ,
  });
  const setRead = (notification: InboxItem, isRead: boolean) =>
    void markRead({
      variables: {
        input: { notificationIdentifier: notification.identifier, isRead },
      },
      refetchQueries: [Queries.INBOX_CONNECTION_QUERY],
    });

  return (
    <Popper
      isOpenState={[isOpen, setIsOpen]}
      placement="bottom-end"
      anchor={
        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              variant="outline"
              // Keep in lockstep with AppTopbarPanelToggles / Create (32px).
              size="icon-sm"
              aria-label="Notifications"
              data-testid="topbar-notifications"
              className="relative"
            >
              <Icon ReactIcon={BellIcon} size="sm" />
              {/* The unread count, out on the button's corner, clear of the
                  bell: up to 99, then "99+". */}
              {unread > 0 ? (
                <Badge
                  variant="destructive"
                  data-testid="topbar-notifications-count"
                  className={cn(
                    "absolute -top-0.75 -right-1 h-4 min-w-4 px-1 tabular-nums",
                    // Pops in as it appears.
                    "animate-[topbar-notifications-badge-in_300ms_cubic-bezier(0.34,1.56,0.64,1)] motion-reduce:[animation:none]",
                  )}
                >
                  {unread > 99 ? "99+" : unread}
                </Badge>
              ) : null}
            </Button>
          </TooltipTrigger>
          <TooltipContent side="bottom">Notifications</TooltipContent>
        </Tooltip>
      }
      floatingStyle={{ width: "400px", padding: 0 }}
    >
      <div className="h-115 flex flex-col">
        <SelectableCardListSearchField
          align="inset-rows"
          placeholder="Search notifications"
          value={term}
          onChange={setTerm}
          inputTestId="topbar-notifications-search"
          // Framed as a list's search is: a bar of its own, ruled off.
          containerClassName="h-12 shrink-0 self-stretch [border-bottom:1px_solid_var(--border)]"
          trailing={
            <>
              <ToggleGroup
                type="single"
                size="sm"
                variant="outline"
                value={shown}
                // Pressing the one shown again would leave none; the list
                // always shows one of them, so that press changes nothing.
                onValueChange={(value) =>
                  value && setShown(value as "all" | "unread" | "read")
                }
              >
                <ToggleGroupItem value="all">All</ToggleGroupItem>
                <ToggleGroupItem value="unread">Unread</ToggleGroupItem>
                <ToggleGroupItem value="read">Read</ToggleGroupItem>
              </ToggleGroup>
              {unread > 0 ? (
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      variant="ghost"
                      size="icon-sm"
                      aria-label="Mark all read"
                      onClick={() =>
                        entityIdentifier &&
                        void markAllRead({
                          variables: {
                            input: { entityIdentifiers: [entityIdentifier] },
                          },
                          refetchQueries: [Queries.INBOX_CONNECTION_QUERY],
                        })
                      }
                    >
                      <Icon ReactIcon={CircleCheckIcon} size="md" />
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent side="bottom">Mark all read</TooltipContent>
                </Tooltip>
              ) : null}
            </>
          }
        />
        <SelectableCardList
          size="sm"
          variant="outlined"
          layout="cards"
          disableVirtualization
          className="flex-1 min-h-0"
          items={notifications}
          loading={loading && notifications.length === 0}
          placeholderItem={NOTIFICATION_PLACEHOLDER}
          loadMore={{
            hasMore: page?.pageInfo.hasNextPage ?? false,
            loading,
            onLoadMore: () =>
              void fetchMore({
                variables: { after: page?.pageInfo.endCursor },
              }),
          }}
          getItemKey={(notification) => notification.id}
          onItemClick={(notification) => {
            const thread = notification.thread;
            // An export opens where it was written: its file, or its Google Sheet.
            const url =
              notification.bulkOperation?.file?.url ??
              notification.bulkOperation?.spreadsheetUrl;
            if (thread) {
              State.getState().openThreadTab(thread.id, { label: thread.name });
              setIsOpen(false);
            } else if (url) {
              window.open(url, "_blank", "noopener,noreferrer");
            } else {
              return;
            }
            // Opening what it is about is taking it in.
            if (!notification.isRead) {
              setRead(notification, true);
            }
          }}
          emptyState={
            search
              ? {
                  title: "Nothing matches",
                  description: "No notification says that.",
                }
              : shown === "unread"
                ? {
                    title: "All caught up",
                    description: "Every notification has been read.",
                  }
                : shown === "read"
                  ? {
                      title: "Nothing read yet",
                      description: "A notification you have read stays here.",
                    }
                  : {
                      title: "Nothing yet",
                      description:
                        "A list you export or delete is heard of here once it is done, and an email forwarded to a business you are in.",
                    }
          }
          renderItem={({ item: notification, isLoading }) => {
            const bulkOperation = notification.bulkOperation;
            const isExport =
              bulkOperation?.operation === BulkOperationEnum.Export;
            // Whose rows it reached: two names, and how many more.
            const names = (bulkOperation?.entities ?? []).map(
              (entity) => entity.name,
            );
            const owners =
              names.length > 2
                ? `${names.slice(0, 2).join(", ")} +${names.length - 2}`
                : names.join(", ");
            // By where the operation is, not by whether a file came back.
            const status =
              bulkOperation == null
                ? notification.thread
                  ? "Open chat"
                  : null
                : bulkOperation.status === ExecutionStatusEnum.Failed
                  ? isExport
                    ? "Could not be written"
                    : "Nothing was deleted"
                  : bulkOperation.status === ExecutionStatusEnum.Succeeded
                    ? isExport && bulkOperation.file?.url
                      ? "Download CSV"
                      : isExport && bulkOperation.spreadsheetUrl
                        ? "Open Google Sheet"
                        : null
                    : isExport
                      ? "Preparing…"
                      : "Deleting…";
            return (
              <SelectableListItemRow
                overline={
                  owners ? (
                    <SelectableListItemRowOverline>
                      {owners}
                    </SelectableListItemRowOverline>
                  ) : null
                }
                overlineEnd={
                  <SelectableListItemRowTitleEnd>
                    {Utils.fTimeAgo(notification.createdAt)}
                  </SelectableListItemRowTitleEnd>
                }
                title={notification.message || "Notification"}
                description={status}
                emphasized={!notification.isRead}
                trailing={
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button
                        size="icon-xs"
                        variant="ghost"
                        // An unread one's envelope is red, as the bell's count is.
                        className={cn(
                          !notification.isRead &&
                            "text-destructive hover:text-destructive",
                        )}
                        aria-label={
                          notification.isRead
                            ? "Mark as unread"
                            : "Mark as read"
                        }
                        data-testid="topbar-notification-read-toggle"
                        onClick={(event) => {
                          event.stopPropagation();
                          setRead(notification, !notification.isRead);
                        }}
                      >
                        <Icon
                          ReactIcon={
                            notification.isRead ? MailOpenIcon : MailIcon
                          }
                          size="sm"
                        />
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent side="bottom">
                      {notification.isRead ? "Mark as unread" : "Mark as read"}
                    </TooltipContent>
                  </Tooltip>
                }
                isLoading={isLoading}
                rootDataAttrs={{
                  "data-testid": "topbar-notification",
                  "data-bulk-operation-status": bulkOperation?.status,
                  "data-read": String(notification.isRead),
                }}
              />
            );
          }}
        />
      </div>
    </Popper>
  );
}
